import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shared visual primitives for the trader area, modeled on the
 * mobile-trader-template look (frosted glass, indigo/purple accents,
 * emerald/red P&L). All are theme-aware (light + dark) via the
 * .trader-* utility classes defined in index.css.
 */

/** Frosted glass surface card. */
export const GlassCard: React.FC<React.HTMLAttributes<HTMLDivElement> & { strong?: boolean }> = ({
    strong,
    className,
    children,
    ...props
}) => {
    return (
        <div
            className={cn(
                strong ? 'trader-glass-strong' : 'trader-glass',
                'rounded-3xl',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

/** Small rounded chip showing a P&L value/percentage with a direction arrow. */
export const PnlPill: React.FC<{
    positive: boolean;
    children: React.ReactNode;
    className?: string;
    showArrow?: boolean;
}> = ({ positive, children, className, showArrow = true }) => {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold',
                positive ? 'trader-pill-positive' : 'trader-pill-negative',
                className
            )}
        >
            {showArrow && (positive ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />)}
            {children}
        </span>
    );
};

/** A single quick-action button: gradient/glass icon tile with a label below. */
export const QuickAction: React.FC<{
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    /** Use the indigo->purple gradient tile (for the primary action). */
    primary?: boolean;
}> = ({ icon, label, onClick, primary }) => {
    return (
        <button type="button" onClick={onClick} className="flex flex-col items-center gap-2 group">
            <span
                className={cn(
                    'w-12 h-12 rounded-2xl flex items-center justify-center text-white',
                    'transition-all group-hover:scale-105 group-active:scale-95',
                    primary
                        ? 'trader-fab-gradient shadow-[0_8px_16px_rgba(99,102,241,0.35)]'
                        : 'trader-glass-strong !text-foreground'
                )}
            >
                {icon}
            </span>
            <span className="text-xs text-muted-foreground">{label}</span>
        </button>
    );
};

/** Row wrapper for QuickAction buttons. */
export const QuickActions: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className,
}) => {
    return (
        <div className={cn('flex items-stretch justify-between gap-3', className)}>{children}</div>
    );
};
